import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  alpha,
  useTheme,
  Fade,
  Grow,
  LinearProgress,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApi } from '../../hooks/useApi';

const AUDIT_DEFINITIONS = [
  {
    "Section": "Network",
    "Title": "Domain Registration and Management",
    "Question": "Are all domain registrations properly tracked and managed? Are billed domains actually registered and in use?",
    "FunctionPath": "cma/audit/network/Test-DomainsTracking.ps1"
  },
  {
    "Section": "Network",
    "Title": "Firewall Management Access",
    "Question": "Is management access protected by multifactor authentication and/or IP or device restrictions?",
    "FunctionPath": "cma/audit/network/Test-FirewallManagementAccess.ps1"
  },
  {
    "Section": "Network",
    "Title": "Support & Licensing",
    "Question": "Do all firewalls have valid support and licensing?",
    "FunctionPath": "cma/audit/network/Test-FirewallSupport.ps1"
  },
  {
    "Section": "Network",
    "Title": "Network Device Configuration Backup",
    "Question": "Are configurations of all network devices regularly backed up?",
    "FunctionPath": "cma/audit/network/Test-NetworkBackupDevices.ps1"
  },
  {
    "Section": "Network",
    "Title": "Network Capacity and Performance",
    "Question": "Is the bandwidth sufficient to support the network environment? Are there latency or packet loss issues?",
    "FunctionPath": "cma/audit/network/Test-NetworkCapacity.ps1"
  },
  {
    "Section": "Network",
    "Title": "Network Device Monitoring",
    "Question": "Are all network devices being monitored in PRTG?",
    "FunctionPath": "cma/audit/network/Test-NetworkMonitor.ps1"
  },
  {
    "Section": "Network",
    "Title": "WiFi Coverage + Capacity",
    "Question": "Is wireless coverage sufficient for the space and the number of devices?",
    "FunctionPath": "cma/audit/network/Test-NetworkWifiCoverage.ps1"
  },
  {
    "Section": "Network",
    "Title": "Guest WiFi Network Configuration",
    "Question": "Is a fully segregated guest network configured and used?",
    "FunctionPath": "cma/audit/network/Test-NetworkWifiGuest.ps1"
  },
  {
    "Section": "Network",
    "Title": "WiFi Encryption Security",
    "Question": "Is encryption used for Wi-Fi access? Confirm that either WPA2 or certificate encryption is used to connect to the Wi-Fi network.",
    "FunctionPath": "cma/audit/network/Test-NetworkWifiSecurity.ps1"
  }
];

function CreateAudit({ selectedAccount, onClose }) {
  const jApi = useApi();
  const theme = useTheme();
  const [auditRunId, setAuditRunId] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingJobs, setIsCreatingJobs] = useState(false);
  const [jobProgress, setJobProgress] = useState({ current: 0, total: 0 });
  const [errorMessage, setErrorMessage] = useState(null);

  // Group items by section
  const groupedItems = useMemo(() => {
    const groups = {};
    AUDIT_DEFINITIONS.forEach(item => {
      if (!groups[item.Section]) {
        groups[item.Section] = [];
      }
      groups[item.Section].push(item);
    });
    return groups;
  }, []);

  const handleToggleItem = (functionPath) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(functionPath)) {
      newSelected.delete(functionPath);
    } else {
      newSelected.add(functionPath);
    }
    setSelectedItems(newSelected);
  };

  const handleToggleSection = (section) => {
    const sectionItems = groupedItems[section];
    const allSelected = sectionItems.every(item => selectedItems.has(item.FunctionPath));

    const newSelected = new Set(selectedItems);
    if (allSelected) {
      // Deselect all in section
      sectionItems.forEach(item => newSelected.delete(item.FunctionPath));
    } else {
      // Select all in section
      sectionItems.forEach(item => newSelected.add(item.FunctionPath));
    }
    setSelectedItems(newSelected);
  };

  const handleSave = async () => {
    if (!selectedAccount) {
      setErrorMessage('No account selected');
      return;
    }

    if (!auditRunId.trim()) {
      setErrorMessage('Please enter an Audit Run ID');
      return;
    }

    if (selectedItems.size === 0) {
      setErrorMessage('Please select at least one audit item');
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage(null);

      // Get today's date in yyyy-MM-dd format
      const today = new Date();
      const auditDate = today.toISOString().split('T')[0];

      // Create array of selected audit items
      const selectedAudits = AUDIT_DEFINITIONS.filter(item =>
        selectedItems.has(item.FunctionPath)
      );

      // Phase 1: Create all audit records
      const createdAudits = [];
      const promises = selectedAudits.map((item, index) => {
        // Generate ID: AuditRunId-001, AuditRunId-002, etc.
        const counter = (index + 1).toString().padStart(3, '0');
        const id = `${auditRunId.trim()}-${counter}`;

        const payload = {
          id: id,
          account: selectedAccount,
          auditRunId: auditRunId.trim(),
          auditDate: auditDate,
          questionId: item.FunctionPath,
          section: item.Section,
          title: item.Title,
          question: item.Question,
          functionPath: item.FunctionPath,
        };

        console.log('PUT cma/audit payload:', payload);
        createdAudits.push({
          id,
          functionPath: item.FunctionPath,
          account: selectedAccount,
          auditRunId: auditRunId.trim(),
          auditDate: auditDate,
        });
        return jApi.request('PUT', 'cma/audit', payload);
      });

      await Promise.all(promises);

      // Phase 2: Create VES jobs and update audits with jobId
      setIsSaving(false);
      setIsCreatingJobs(true);
      setJobProgress({ current: 0, total: createdAudits.length });

      for (let i = 0; i < createdAudits.length; i++) {
        const audit = createdAudits[i];
        setJobProgress({ current: i + 1, total: createdAudits.length });

        try {
          // Create VES job. Test- scripts now require all four splat fields —
          // they create / upsert their own cma.audit record using AuditRunId
          // and AuditDate to populate the record.
          const vesJobPayload = {
            functionpath: audit.functionPath,
            splat: {
              account:    audit.account,
              auditId:    audit.id,
              auditRunId: audit.auditRunId,
              auditDate:  audit.auditDate,
            },
          };

          console.log('Creating VES job:', vesJobPayload);
          const vesResponse = await jApi.request('POST', 'ves/job', vesJobPayload);
          console.log('VES job created:', vesResponse);

          const jobId = vesResponse.data?.id;

          if (jobId) {
            // Update audit record with jobId
            const updatePayload = {
              id: audit.id,
              jobId: jobId,
            };

            console.log('Updating audit with jobId:', updatePayload);
            await jApi.request('PUT', 'cma/audit', updatePayload);
            console.log('Audit updated with jobId:', jobId);
          }
        } catch (jobError) {
          // Log error but continue with other jobs
          console.error(`Failed to create job for audit ${audit.id}:`, jobError);
        }
      }

      // Success - close and refresh
      onClose();
    } catch (error) {
      console.error('Failed to create audit:', error);

      // Try to extract error message from various possible locations
      let errorMsg = 'Unknown error';
      if (error?.response?.data?.error?.message) {
        errorMsg = error.response.data.error.message;
      } else if (error?.data?.error?.message) {
        errorMsg = error.data.error.message;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      setErrorMessage('Failed to create audit: ' + errorMsg);
    } finally {
      setIsSaving(false);
      setIsCreatingJobs(false);
      setJobProgress({ current: 0, total: 0 });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={onClose}
                sx={{
                  background: alpha(theme.palette.background.paper, 0.6),
                  '&:hover': {
                    background: alpha(theme.palette.background.paper, 0.8),
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Create New Audit
              </Typography>
            </Stack>
          </Box>
        </Fade>

        {errorMessage && (
          <Fade in>
            <Paper
              sx={{
                p: 2,
                mb: 3,
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="error.main">
                {errorMessage}
              </Typography>
            </Paper>
          </Fade>
        )}

        <Grow in timeout={700}>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Audit Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Audit Run ID"
                value={auditRunId}
                onChange={(e) => setAuditRunId(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
              <TextField
                label="Account"
                value={selectedAccount || 'No account selected'}
                fullWidth
                disabled
                variant="outlined"
              />
              <TextField
                label="Audit Date"
                value={new Date().toISOString().split('T')[0]}
                fullWidth
                disabled
                variant="outlined"
              />
            </Stack>
          </Paper>
        </Grow>

        <Grow in timeout={900}>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Select Audit Items ({selectedItems.size} selected)
            </Typography>

            {Object.entries(groupedItems).map(([section, items]) => {
              const sectionSelectedCount = items.filter(item =>
                selectedItems.has(item.FunctionPath)
              ).length;
              const allSelected = sectionSelectedCount === items.length;
              const someSelected = sectionSelectedCount > 0 && !allSelected;

              return (
                <Accordion
                  key={section}
                  defaultExpanded
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    '&:before': { display: 'none' },
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 2px 8px ${alpha(theme.palette.common.black, 0.3)}`
                      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleSection(section);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Typography variant="h6" fontWeight="bold">
                        {section} ({sectionSelectedCount}/{items.length})
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      p: 0,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                    }}
                  >
                    <List>
                      {items.map((item, index) => (
                        <ListItem
                          key={item.FunctionPath}
                          sx={{
                            borderBottom: index < items.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedItems.has(item.FunctionPath)}
                                onChange={() => handleToggleItem(item.FunctionPath)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {item.Title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {item.Question}
                                </Typography>
                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                  {item.FunctionPath}
                                </Typography>
                              </Box>
                            }
                            sx={{ width: '100%', m: 0, py: 1 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        </Grow>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pb: 4 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSaving || isCreatingJobs}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving || isCreatingJobs || !auditRunId.trim() || selectedItems.size === 0}
            size="large"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              },
            }}
          >
            {isSaving ? 'Creating Audits...' : isCreatingJobs ? `Creating Jobs: ${jobProgress.current}/${jobProgress.total}` : 'Save Audit'}
          </Button>
        </Box>

        {(isSaving || isCreatingJobs) && (
          <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
            <LinearProgress
              variant={isCreatingJobs ? "determinate" : "indeterminate"}
              value={isCreatingJobs ? (jobProgress.current / jobProgress.total) * 100 : undefined}
            />
            {isCreatingJobs && (
              <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Creating VES jobs: {jobProgress.current} of {jobProgress.total}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default CreateAudit;
